import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { portalService } from '../services/portalService';

const INTAKE_FIELDS = [
  { key: 'fullName', label: 'Full name' },
  { key: 'dob', label: 'Date of birth' },
  { key: 'phoneNumber', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'emergencyContact', label: 'Emergency contact' },
  { key: 'chiefComplaint', label: 'Chief complaint', textarea: true },
  { key: 'currentStressors', label: 'Current stressors', textarea: true },
  { key: 'visitMotivation', label: 'What brings you in?', textarea: true },
  { key: 'currentMedications', label: 'Current medications', textarea: true },
  { key: 'pastDiagnoses', label: 'Past diagnoses', textarea: true },
];

const ClientPortal = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [intakeForm, setIntakeForm] = useState({});
  const [savingIntake, setSavingIntake] = useState(false);
  const [intakeSaved, setIntakeSaved] = useState(false);
  const [messageBody, setMessageBody] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [apptReq, setApptReq] = useState({
    preferredStartAt: '',
    preferredType: 'therapy',
    notes: '',
  });
  const [sendingAppt, setSendingAppt] = useState(false);
  const [apptSent, setApptSent] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await portalService.view(token);
      setData(res.data);
      setIntakeForm(res.data?.intake || {});
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Unable to open portal link');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  const saveIntake = async (e) => {
    e.preventDefault();
    try {
      setSavingIntake(true);
      setIntakeSaved(false);
      const res = await portalService.submitIntake(token, intakeForm);
      setIntakeForm(res.data || intakeForm);
      setIntakeSaved(true);
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to save intake');
    } finally {
      setSavingIntake(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!messageBody.trim()) return;
    try {
      setSendingMsg(true);
      await portalService.sendMessage(token, { body: messageBody });
      setMessageBody('');
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to send message');
    } finally {
      setSendingMsg(false);
    }
  };

  const sendApptRequest = async (e) => {
    e.preventDefault();
    try {
      setSendingAppt(true);
      setApptSent(false);
      await portalService.requestAppointment(token, {
        ...apptReq,
        preferredStartAt: apptReq.preferredStartAt || undefined,
      });
      setApptSent(true);
      setApptReq({ preferredStartAt: '', preferredType: 'therapy', notes: '' });
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to request appointment');
    } finally {
      setSendingAppt(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl shadow p-8">
          <h1 className="text-2xl font-semibold text-primary mb-1">Client Portal</h1>
          <p className="text-sm text-gray-500 mb-6">Peleescribe</p>

          {loading && <p>Loading…</p>}
          {error && <p className="text-red-600">{error}</p>}

          {data && (
            <>
              <p className="mb-2">
                Hello{data.patient?.fullName ? `, ${data.patient.fullName}` : ''}.
              </p>
              <p className="text-sm text-gray-600">{data.notice}</p>
            </>
          )}
        </div>

        {data && (
          <>
            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="font-medium mb-3">Appointments</h2>
              {(data.appointments || []).length === 0 ? (
                <p className="text-gray-500 text-sm">No appointments to show.</p>
              ) : (
                <ul className="space-y-3">
                  {data.appointments.map((a) => (
                    <li key={a._id} className="border rounded-lg p-3 text-sm">
                      <div className="font-medium">{a.title}</div>
                      <div className="text-gray-600">
                        {a.startAt ? new Date(a.startAt).toLocaleString() : '—'}
                        {a.type ? ` · ${a.type}` : ''}
                        {a.status ? ` · ${a.status}` : ''}
                      </div>
                      {a.isTelehealth && a.telehealthUrl && (
                        <a
                          href={a.telehealthUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary underline text-xs mt-1 inline-block"
                        >
                          Join telehealth
                        </a>
                      )}
                      {a.location && !a.isTelehealth && (
                        <div className="text-xs text-gray-500 mt-1">{a.location}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <form onSubmit={sendApptRequest} className="mt-4 space-y-2 border-t pt-4">
                <h3 className="text-sm font-medium">Request an appointment</h3>
                <input
                  type="datetime-local"
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={apptReq.preferredStartAt}
                  onChange={(e) => setApptReq({ ...apptReq, preferredStartAt: e.target.value })}
                />
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={apptReq.preferredType}
                  onChange={(e) => setApptReq({ ...apptReq, preferredType: e.target.value })}
                >
                  <option value="therapy">Therapy</option>
                  <option value="intake">Intake</option>
                  <option value="assessment">Assessment</option>
                  <option value="follow_up">Follow-up</option>
                </select>
                <textarea
                  rows={2}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="Notes for your clinician (optional)"
                  value={apptReq.notes}
                  onChange={(e) => setApptReq({ ...apptReq, notes: e.target.value })}
                />
                <button
                  type="submit"
                  disabled={sendingAppt}
                  className="bg-primary text-white px-4 py-2 rounded-lg text-sm"
                >
                  {sendingAppt ? 'Sending…' : 'Submit request'}
                </button>
                {apptSent && (
                  <span className="text-green-700 text-sm ml-2">Request sent</span>
                )}
              </form>
            </div>

            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="font-medium mb-3">Intake questionnaire</h2>
              <p className="text-xs text-gray-500 mb-4">
                Your clinician will review these answers. This does not generate a clinical report by itself.
              </p>
              <form onSubmit={saveIntake} className="space-y-3">
                {INTAKE_FIELDS.map((f) => (
                  <label key={f.key} className="flex flex-col gap-1 text-sm">
                    {f.label}
                    {f.textarea ? (
                      <textarea
                        rows={2}
                        className="border rounded px-3 py-2"
                        value={intakeForm[f.key] || ''}
                        onChange={(e) => setIntakeForm({ ...intakeForm, [f.key]: e.target.value })}
                      />
                    ) : (
                      <input
                        className="border rounded px-3 py-2"
                        value={intakeForm[f.key] || ''}
                        onChange={(e) => setIntakeForm({ ...intakeForm, [f.key]: e.target.value })}
                      />
                    )}
                  </label>
                ))}
                <button
                  type="submit"
                  disabled={savingIntake}
                  className="bg-primary text-white px-4 py-2 rounded-lg text-sm"
                >
                  {savingIntake ? 'Saving…' : 'Save intake'}
                </button>
                {intakeSaved && <span className="text-green-700 text-sm ml-3">Saved</span>}
              </form>
            </div>

            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="font-medium mb-3">Messages</h2>
              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                {(data.messages || []).length === 0 && (
                  <p className="text-sm text-gray-500">No messages yet.</p>
                )}
                {(data.messages || []).map((m) => (
                  <div
                    key={m._id}
                    className={`text-sm border rounded-lg p-3 ${
                      m.senderRole === 'client' ? 'bg-blue-50' : 'bg-gray-50'
                    }`}
                  >
                    <div className="text-xs text-gray-500 mb-1">
                      {m.senderRole === 'client' ? 'You' : 'Clinician'} ·{' '}
                      {m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}
                    </div>
                    <div className="font-medium">{m.subject}</div>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                  </div>
                ))}
              </div>
              <form onSubmit={sendMessage} className="space-y-2">
                <textarea
                  rows={3}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="Write a message to your clinician…"
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={sendingMsg || !messageBody.trim()}
                  className="bg-primary text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                >
                  {sendingMsg ? 'Sending…' : 'Send message'}
                </button>
              </form>
            </div>

            <p className="text-xs text-gray-400 text-center">
              Link expires {data.expiresAt ? new Date(data.expiresAt).toLocaleString() : ''}.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ClientPortal;
